import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  blockRoom,
  clearAccommodationInfo,
  getBedsHistory,
  getCamps,
  markBedClean,
  markRoomClean,
} from 'features/accommodation/accommodationSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { IFlatBed, IHousekeepingRoomOption } from 'types/accommodation.interface'
import useQueryPagination from 'hooks/useQueryPagination'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { JustSpinner } from 'components/Loading/Loading'
import { Tabs, TabsContainer } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Container, ContainerContent, ContainerHeader } from '../Accommodation.style'
import { DirtyIcon } from '../icons/DirtyIcon'
import { BlockedIcon } from '../icons/BlockedIcon'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { BED_BLOCKED, BED_DIRTY, buildOccupantMap, computeCamps, flattenBeds } from '../accommodationStats'
import {
  SubTabs,
  StatBand,
  SectionTitleRow,
  SectionTitleGroup,
  SectionCount,
  Caption,
  BedRow,
  BedIdentity,
  BedLabel,
  BedLocation,
  RowActions,
  EmptyState,
  LoaderWrap,
  CampBreakdown,
  CampChip,
  CampChipCount,
  BlockRoomBar,
  BlockRoomHint,
  RoomSelect,
} from './AccommodationHousekeeping.styled'

const C = {
  dirty: tokens.status.warning.fg,
  blocked: tokens.status.danger.fg,
}

const BedLocationLine = ({ bed }: { bed: IFlatBed }) => (
  <BedLocation>
    <LocationIcon />
    {bed.campName || '/'}
    <NextIcon />
    {bed.blockName || '/'}
    <NextIcon />
    {bed.roomName || '/'}
  </BedLocation>
)

export const AccommodationHousekeeping = () => {
  const [params] = useState({ page: 1, limit: 99 })
  const [tab, setTab] = useState<'Cleaning' | 'Blocked'>('Cleaning')
  const [selectedRoom, setSelectedRoom] = useState<number | ''>('')
  const dispatch = useDispatch<AppDispatch>()
  const { camps, bedsHistory, isLoading } = useSelector((store: RootState) => store.accomodation)

  useQueryPagination({
    fetchFunctions: [getCamps],
    params,
    cleanUpFunctions: [clearAccommodationInfo],
  })

  useEffect(() => {
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const refetch = () => {
    dispatch(getCamps({ page: 1, limit: 99 }))
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
  }

  const occupantByBed = useMemo(() => buildOccupantMap(bedsHistory?.data || []), [bedsHistory])
  const beds = useMemo(
    () => flattenBeds(computeCamps(camps?.data || [], occupantByBed)),
    [camps, occupantByBed],
  )

  const roomOptions = useMemo<IHousekeepingRoomOption[]>(() => {
    const options: IHousekeepingRoomOption[] = []
    ;(camps?.data || []).forEach((camp) => {
      ;(camp.blocks || []).forEach((block) => {
        ;(block.rooms || []).forEach((room) => {
          options.push({
            roomId: room.id,
            campName: camp.name,
            blockName: block.name,
            roomName: room.label,
          })
        })
      })
    })
    return options
  }, [camps])

  const dirtyBeds = useMemo(() => beds.filter((b) => b.effectiveState === BED_DIRTY), [beds])
  const blockedBeds = useMemo(() => beds.filter((b) => b.effectiveState === BED_BLOCKED), [beds])

  const dirtyByCamp = useMemo(() => {
    const counts = new Map<string, number>()
    dirtyBeds.forEach((b) => {
      const key = b.campName || 'Unassigned'
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [dirtyBeds])

  const handleMarkClean = (bedId: number, word: string) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(markBedClean(bedId))
          refetch()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word,
      }),
    )
  }

  const handleReturnToService = (bed: IFlatBed) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(bed.outOfService ? markRoomClean(bed.roomId) : markBedClean(bed.bedId))
          refetch()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'return this bed to service',
      }),
    )
  }

  const handleBlockRoom = () => {
    if (selectedRoom === '') return
    const roomId = selectedRoom
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(blockRoom(roomId))
          setSelectedRoom('')
          refetch()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'block this room',
      }),
    )
  }

  return (
    <>
      <SubTabs>
        <TabsContainer>
          <Tabs active={tab === 'Cleaning'} onClick={() => setTab('Cleaning')}>
            Cleaning
          </Tabs>
          <Tabs active={tab === 'Blocked'} onClick={() => setTab('Blocked')}>
            Blocked
          </Tabs>
        </TabsContainer>
      </SubTabs>

      {tab === 'Cleaning' ? (
        <>
          <StatBand>
            <MetricCard
              layout='inline'
              label='Dirty beds'
              value={String(dirtyBeds.length)}
              icon={<DirtyIcon />}
              accentColor={C.dirty}
            />
          </StatBand>

          {dirtyByCamp.length > 0 && (
            <CampBreakdown>
              {dirtyByCamp.map(([camp, count]) => (
                <CampChip key={camp}>
                  {camp}
                  <CampChipCount>{count}</CampChipCount>
                </CampChip>
              ))}
            </CampBreakdown>
          )}

          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <DirtyIcon />
                  <TitleText>To clean</TitleText>
                  <SectionCount>{dirtyBeds.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <ContainerContent noHeader>
              <Caption>Dirty beds awaiting turnaround. Mark clean when a bed is serviced.</Caption>
              {isLoading && !beds.length ? (
                <LoaderWrap>
                  <JustSpinner />
                </LoaderWrap>
              ) : dirtyBeds.length ? (
                dirtyBeds.map((bed) => (
                  <BedRow key={bed.bedId}>
                    <BedIdentity>
                      <BedLabel>{bed.label}</BedLabel>
                    </BedIdentity>
                    <BedLocationLine bed={bed} />
                    <StatusPill status='warning' dot>
                      Dirty
                    </StatusPill>
                    <RowActions>
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={() => handleMarkClean(bed.bedId, 'mark this bed as clean')}
                      >
                        Mark clean
                      </Button>
                    </RowActions>
                  </BedRow>
                ))
              ) : (
                <EmptyState>Nothing to clean right now.</EmptyState>
              )}
            </ContainerContent>
          </Container>
        </>
      ) : (
        <>
          <StatBand>
            <MetricCard
              layout='inline'
              label='Blocked beds'
              value={String(blockedBeds.length)}
              icon={<BlockedIcon />}
              accentColor={C.blocked}
            />
          </StatBand>

          {roomOptions.length > 0 && (
            <BlockRoomBar>
              <BlockRoomHint>Take a room out of service.</BlockRoomHint>
              <RoomSelect
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value ? Number(e.target.value) : '')}
              >
                <option value=''>Select a room…</option>
                {roomOptions.map((room) => (
                  <option key={room.roomId} value={room.roomId}>
                    {room.campName} · {room.blockName} · {room.roomName}
                  </option>
                ))}
              </RoomSelect>
              <Button
                variant='danger'
                size='sm'
                disabled={selectedRoom === ''}
                onClick={handleBlockRoom}
              >
                Block room
              </Button>
            </BlockRoomBar>
          )}

          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <BlockedIcon />
                  <TitleText>Out of service</TitleText>
                  <SectionCount>{blockedBeds.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <ContainerContent noHeader>
              <Caption>Beds out of service. Blocked capacity is unavailable for check-in.</Caption>
              {isLoading && !beds.length ? (
                <LoaderWrap>
                  <JustSpinner />
                </LoaderWrap>
              ) : blockedBeds.length ? (
                blockedBeds.map((bed) => (
                  <BedRow key={bed.bedId}>
                    <BedIdentity>
                      <BedLabel>{bed.label}</BedLabel>
                    </BedIdentity>
                    <BedLocationLine bed={bed} />
                    <StatusPill status='danger' dot>
                      Blocked
                    </StatusPill>
                    <RowActions>
                      <Button
                        variant='secondary'
                        size='sm'
                        onClick={() => handleReturnToService(bed)}
                      >
                        Return to service
                      </Button>
                    </RowActions>
                  </BedRow>
                ))
              ) : (
                <EmptyState>No blocked beds.</EmptyState>
              )}
            </ContainerContent>
          </Container>
        </>
      )}
    </>
  )
}
